import React, { useState, useCallback } from "react";
import { Menu, X } from "lucide-react";
import DocSidebar from "../components/apiDocs/DocSidebar";
import EndpointDoc from "../components/apiDocs/EndpointDoc";
import ApiPlayground from "../components/apiDocs/ApiPlayground";
import GettingStarted from "../components/apiDocs/GettingStarted";
import AuthSection from "../components/apiDocs/AuthSection";
import ErrorReference from "../components/apiDocs/ErrorReference";
import TemplateSendingGuide from "../components/apiDocs/TemplateSendingGuide";
import {
  API_CATEGORIES,
  ENDPOINTS,
  ERROR_CODES,
  GETTING_STARTED,
  AUTH_SECTION,
  TEMPLATE_GUIDE,
} from "../data/apiDocsData";

const ApiDocs = () => {
  const [activeSection, setActiveSection] = useState("introduction");
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showPlayground, setShowPlayground] = useState(true);

  const handleSelect = useCallback((id) => {
    setActiveSection(id);
    setShowMobileSidebar(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const currentEndpoint = ENDPOINTS[activeSection] || null;

  const renderMainContent = () => {
    switch (activeSection) {
      case "introduction":
        return <GettingStarted data={GETTING_STARTED} />;
      case "api-key-usage":
        return <AuthSection data={AUTH_SECTION} />;
      case "error-codes":
        return <ErrorReference errors={ERROR_CODES} />;
      case "template-guide":
        return <TemplateSendingGuide guide={TEMPLATE_GUIDE} />;
      default:
        if (currentEndpoint) {
          return <EndpointDoc endpoint={currentEndpoint} />;
        }
        return <GettingStarted data={GETTING_STARTED} />;
    }
  };

  const playgroundEndpoint = ENDPOINTS[activeSection] || null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="border-b border-gray-200 bg-white px-4 py-2.5 flex items-center justify-between lg:hidden">
        <button
          onClick={() => setShowMobileSidebar(!showMobileSidebar)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
        >
          {showMobileSidebar ? <X size={18} /> : <Menu size={18} />}
        </button>
        <span className="text-sm font-semibold text-gray-800">API Documentation</span>
        <button
          onClick={() => setShowPlayground(!showPlayground)}
          className="px-2.5 py-1 rounded-lg text-[11px] font-medium bg-[#048AAD]/10 text-[#048AAD]"
        >
          {showPlayground ? "Hide" : "Show"} Playground
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className={`
          fixed inset-y-0 left-0 z-30 transform transition-transform lg:relative lg:translate-x-0
          ${showMobileSidebar ? "translate-x-0" : "-translate-x-full"}
        `}>
          <DocSidebar
            categories={API_CATEGORIES}
            activeSection={activeSection}
            onSelect={handleSelect}
          />
        </div>

        {showMobileSidebar && (
          <div
            className="fixed inset-0 bg-black/20 z-20 lg:hidden"
            onClick={() => setShowMobileSidebar(false)}
          />
        )}

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-6 py-8">
            {renderMainContent()}
          </div>
        </main>

        {showPlayground && (
          <div className="hidden lg:flex w-96 flex-shrink-0 border-l border-gray-200 bg-white flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-white">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400">API Playground</h3>
            </div>
            <div className="flex-1 overflow-hidden">
              <ApiPlayground endpoint={playgroundEndpoint} />
            </div>
          </div>
        )}
      </div>

      {showPlayground && (
        <div className="lg:hidden border-t border-gray-200 bg-white">
          <details className="group">
            <summary className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-400 cursor-pointer list-none flex items-center justify-between">
              API Playground
              <span className="text-[10px] font-normal normal-case tracking-normal text-gray-400 group-open:hidden">Tap to expand</span>
            </summary>
            <div className="h-[500px] overflow-hidden">
              <ApiPlayground endpoint={playgroundEndpoint} />
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default ApiDocs;
